import { useEffect, useMemo, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Menu, Wrapper, Scrollable } from './styled';
import Icon from '../../../../common/Icon';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
type Props = {
    src: string;
    isOpen: boolean;
    toggle: () => void;
};

export function ReferenceViewer(props: Props) {
    const { src, isOpen, toggle } = props;
    const [numPages, setNumPages] = useState<number>();
    const [visiblePages, setVisiblePages] = useState<number[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [deviceInfo, setDeviceInfo] = useState({
        isMobile: false,
        isTouch: false,
        maxPages: 50
    })

    useEffect(() => {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const isMobileUserAgent = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isSmallScreen = window.innerWidth <= 768;

        const isMobile = isMobileUserAgent || (isTouchDevice && isSmallScreen);
        let maxPages = 50;
        if (isMobile) {
            maxPages = 8;
        } else if (isTouchDevice) {
            maxPages = 12;  // Tablets
        }

        setDeviceInfo({
            isMobile,
            isTouch: isTouchDevice,
            maxPages
        });
    }, []);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
    }

    function addVisiblePage(page: number) {
        setVisiblePages(prev => {
            // If page already exists, move it to the end (most recent)
            const filtered = prev.filter(p => p !== page);
            const updated = [...filtered, page];

            // If we exceed the cap, remove oldest pages
            if (updated.length > deviceInfo.maxPages) {
                return updated.slice(-deviceInfo.maxPages);
            }

            return updated;
        });
    }

    const isPageLoaded = (pageNumber: number) => visiblePages.includes(pageNumber);
    const pages = useMemo(() => {
        if (!numPages) return [];
        
        return Array.from(new Array(numPages), (_, index) => {
            const pageNumber = index + 1;
            return (
                <PageContainer
                    key={pageNumber}
                    pageNumber={pageNumber}
                    onVisible={addVisiblePage}
                >
                    {isPageLoaded(pageNumber) ? (
                        <Page
                            pageNumber={pageNumber}
                        />
                    ): (
                        <div style={{ height: '1000px', background: '#ffffffff' }}>                            
                        </div>
                    )}
                </PageContainer>
            )
        })
    }, [numPages, visiblePages]);

    /**
     * Loads pages dynamically when they're clicked in the document.
     * @param param0 
     */
    const handleItemClick = ({ dest, pageNumber }: {
        dest?: any;
        pageNumber?: number;        
    }) => {                            
        if(pageNumber && pageNumber !== currentPage) {
            addVisiblePage(pageNumber);
            setTimeout(() => {
                const targetElement = document.getElementById(`pdf-page-${pageNumber}`);
                if (targetElement) {
                    targetElement.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start' 
                    });
                }
            }, visiblePages.includes(pageNumber) ? 0 : 300);
            setCurrentPage(pageNumber);
        }
    }        

    

    return (
        <Wrapper>
            <Menu onClick={toggle}>
                <Icon name={isOpen ? 'chevronRight' : 'chevronLeft'} />
            </Menu>
            <Scrollable $isOpen={isOpen}>
                <Document 
                    file={src}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onItemClick={handleItemClick}
                >
                    {pages}
                </Document>
            </Scrollable>
        </Wrapper>
    );
}

// Helper component with Intersection Observer
function PageContainer({ 
    children, 
    pageNumber, 
    onVisible 
}: { 
    children: React.ReactNode; 
    pageNumber: number; 
    onVisible: (page: number) => void; 
}) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    onVisible(pageNumber);
                }
            },
            { rootMargin: '100px' } // Load 100px before visible
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [pageNumber, onVisible]);

    return <div ref={ref} id={`pdf-page-${pageNumber}`}>{children}</div>;
}